import { ECarCondition, getECarConditionList } from '@if/core/types'
import { DeleteForever as DeleteForeverIcon, Search as SearchIcon } from '@mui/icons-material'
import {
  Alert,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  IconButton,
  LinearProgress,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material'
import { getApiClient } from '@src/services/api-client'
import { formatCar } from '@src/services/Formatter'
import { ChangeEvent, useMemo, useState } from 'react'
import {
  Control,
  FieldError,
  FieldErrorsImpl,
  Merge,
  UseFormGetValues,
  UseFormRegister,
  UseFormSetValue,
  UseFormWatch,
  useWatch,
} from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { InputTextField, MultipleSelectCheckmarks, SelectComponent } from '../../../components'
import vehicleDecoderApi from '../../../data/api/vehicle-decoder/vehicle-decoder-api'
import { useAppSelector } from '../../../data/store'
import { userSelectors } from '../../../data/store/UserStore'
import { Additions, AutoWorksheet, Merchant, SelectValueListItem, Vehicle, VehicleSchema } from '../../../data/types'

interface Props {
  defaultVehicle: Vehicle | undefined | null
  merchant: Merchant | null
  setValue: UseFormSetValue<AutoWorksheet>
  getValues: UseFormGetValues<AutoWorksheet>
  control: Control<AutoWorksheet>
  register: UseFormRegister<AutoWorksheet>
  watch: UseFormWatch<AutoWorksheet>
  errors: Merge<FieldError, FieldErrorsImpl<Vehicle>> | undefined
  isTradeIn?: boolean
  name: 'vehicle' | 'tradeInVehicle'
  title: string
  editDisabled: boolean
}

const apiClient = getApiClient()

const VehicleForm = ({
  defaultVehicle,
  merchant,
  setValue,
  getValues,
  control,
  register,
  watch,
  errors,
  isTradeIn,
  name,
  title,
  editDisabled,
}: Props) => {
  const { t } = useTranslation()
  const user = useAppSelector(userSelectors.getUser)
  const [decodedVehicles, setDecodedVehicles] = useState<Vehicle[]>([])
  const [selectedVehicle, setSelectedVehicle] = useState<Vehicle | undefined | null>(defaultVehicle)
  const [notFound, setNotFound] = useState(false)
  const [isDecoding, setIsDecoding] = useState(false)

  const thirdPartySearchOverrideWatch = useWatch({
    control,
    name: 'vehicle.thirdPartySearchOverride',
  })

  const resetVehicle = () => {
    const tempVehicle = {
      ...VehicleSchema.getDefault(),
      vin: getValues(`${name}.vin`),
      isTradeIn: getValues('vehicle.isTradeIn'),
    }

    setValue(name, tempVehicle)

    setDecodedVehicles([])
  }

  const handleThirdPartySearchOverrideChanged = (event: ChangeEvent<HTMLInputElement>) => {
    setValue(`${name}.thirdPartySearchOverride`, event.target.checked)
    if (event.target.checked === false) resetVehicle()
  }

  const handleAdditionsChanged = (additionCodes: string[] | number[]) => {
    setValue(
      `${name}.additions`,
      selectedVehicle?.additions.filter((x) => additionCodes.some((code) => x.code === code)) ?? ([] as Additions[]),
    )
  }

  const decodeVin = async () => {
    const vin = getValues(`${name}.vin`)
    if (vin?.length === 17 && apiClient && user) {
      setIsDecoding(true)
      resetVehicle()
      await vehicleDecoderApi
        .decode(apiClient, vin, user.id, merchant?.province ?? 'QC')
        .then((ret) => {
          if (ret[0]) {
            const first = ret[0]
            first.vin = vin
            setDecodedVehicles(ret)
            setSelectedVehicle(first)
            setValue(name, first)
            setNotFound(false)
          } else {
            setNotFound(true)
          }
          setIsDecoding(false)
        })
        .catch(() => {
          setIsDecoding(false)
          setNotFound(true)
        })
    }
  }

  const handleVehicleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const vehicle = decodedVehicles.find((v) => v.thirdPartyId && v.thirdPartyId.toString() === event.target.value)
    if (vehicle) {
      vehicle.vin = getValues(`${name}.vin`)
      setSelectedVehicle(vehicle)
      setValue(name, vehicle)
    }
  }

  const carConditionOptions = useMemo(
    () => getECarConditionList().map((item) => ({ label: t(`ECarCondition.${item}`, { ns: 'enum' }), value: item })),
    [t],
  )

  return (
    <>
      <Divider style={{ fontSize: 25 }} flexItem>
        <Typography variant="h4" component="span" gutterBottom>
          {title}
        </Typography>
      </Divider>
      <Grid container marginTop={2} marginBottom={2} spacing={2} sx={{ paddingLeft: 2, paddingRight: 2 }}>
        <Grid
          container
          direction="row"
          justifyContent={isTradeIn && !selectedVehicle ? 'center' : 'space-between'}
          alignItems="center"
          size={12}
        >
          {(selectedVehicle || !isTradeIn) && (
            <Grid
              size={{
                xs: 9,
                md: 2.5,
              }}
            >
              <InputTextField
                slotProps={{
                  input: {
                    endAdornment: (
                      <IconButton onClick={decodeVin}>
                        <SearchIcon />
                      </IconButton>
                    ),
                    style: { textTransform: 'uppercase' },
                  },
                }}
                label={t('worksheet.vin')}
                {...register(`${name}.vin`)}
                error={errors?.vin}
              />
            </Grid>
          )}
          {isTradeIn && (
            <Grid>
              {!selectedVehicle && (
                <Button
                  sx={{ p: 2, mb: 2 }}
                  variant="contained"
                  onClick={() => {
                    setSelectedVehicle(VehicleSchema.getDefault())
                    resetVehicle()
                  }}
                >
                  {t('worksheet.addTradeIn')}
                </Button>
              )}
              {selectedVehicle && (
                <Grid>
                  <IconButton
                    onClick={() => {
                      setSelectedVehicle(null)
                      setValue(name, null)
                    }}
                    disabled={editDisabled}
                  >
                    <DeleteForeverIcon fontSize="large" color={editDisabled ? 'inherit' : 'error'} />
                  </IconButton>
                </Grid>
              )}
            </Grid>
          )}
        </Grid>

        {(selectedVehicle || !isTradeIn) && (
          <>
            <Grid size={12}>
              {notFound ? (
                <Alert severity="error">{t('worksheet.vehicleNotFound')}</Alert>
              ) : (
                <FormControl>
                  {decodedVehicles.length > 0 && (
                    <Typography variant="body1">{t('worksheet.availableVehicles')}</Typography>
                  )}
                  <RadioGroup aria-labelledby="vehicles-label" onChange={(event) => handleVehicleChange(event)}>
                    <Stack>
                      {decodedVehicles.map((v) => (
                        <FormControlLabel
                          checked={selectedVehicle?.thirdPartyId === v.thirdPartyId}
                          key={v.thirdPartyId}
                          value={v.thirdPartyId}
                          control={<Radio />}
                          label={formatCar(v)}
                        />
                      ))}
                    </Stack>
                  </RadioGroup>
                </FormControl>
              )}

              {(notFound || thirdPartySearchOverrideWatch) && (
                <FormGroup row>
                  <FormControlLabel
                    control={
                      <Checkbox
                        defaultChecked={defaultVehicle?.thirdPartySearchOverride}
                        onChange={handleThirdPartySearchOverrideChanged}
                      />
                    }
                    label={t('worksheet.overrideVin')}
                    labelPlacement="end"
                  />
                </FormGroup>
              )}
              {isDecoding && <LinearProgress />}
            </Grid>

            <Grid
              size={{
                xs: 12,
                md: 2.5,
              }}
            >
              <InputTextField label={t('worksheet.mileage')} {...register(`${name}.mileage`)} error={errors?.mileage} />
            </Grid>
            {!isTradeIn && (
              <Grid
                size={{
                  xs: 6,
                  md: 2.5,
                }}
              >
                <SelectComponent
                  items={carConditionOptions}
                  label="Condition"
                  {...register(`${name}.condition`)}
                  error={errors?.condition}
                />
              </Grid>
            )}
            {!isTradeIn && (
              <Grid
                size={{
                  xs: 6,
                  md: 2.5,
                }}
              >
                <MultipleSelectCheckmarks
                  title={t('worksheet.includes')}
                  content={selectedVehicle?.includes.map((item) => ({ label: item, value: item })) ?? []}
                  {...register(`${name}.includes`)}
                  defaultValues={selectedVehicle?.includes}
                />
              </Grid>
            )}
            {!isTradeIn && (
              <Grid
                size={{
                  xs: 6,
                  md: 2.5,
                }}
              >
                <MultipleSelectCheckmarks
                  title={t('worksheet.additions')}
                  content={
                    selectedVehicle?.additions.map((item) => {
                      const data: SelectValueListItem = { label: item.description, value: item.code }
                      return data
                    }) ?? []
                  }
                  defaultValues={selectedVehicle?.additions.map((x) => x.code)}
                  onValueChanged={handleAdditionsChanged}
                />
              </Grid>
            )}

            <Grid
              size={{
                xs: 6,
                md: 2,
              }}
            >
              <InputTextField
                disabled={!thirdPartySearchOverrideWatch}
                label={t('worksheet.year')}
                {...register(`${name}.year`)}
                error={errors?.year}
              />
            </Grid>

            <Grid
              size={{
                xs: 6,
                md: 2.5,
              }}
            >
              <InputTextField
                disabled={!thirdPartySearchOverrideWatch}
                label={t('worksheet.make')}
                {...register(`${name}.make`)}
                error={errors?.make}
              />
            </Grid>

            <Grid
              size={{
                xs: 6,
                md: 2.5,
              }}
            >
              <InputTextField
                disabled={!thirdPartySearchOverrideWatch}
                label={t('worksheet.model')}
                {...register(`${name}.model`)}
                error={errors?.model}
              />
            </Grid>
            {!isTradeIn && (
              <Grid
                size={{
                  xs: 6,
                  md: 2.5,
                }}
              >
                <InputTextField
                  disabled={!thirdPartySearchOverrideWatch}
                  label={t('worksheet.series')}
                  {...register(`${name}.series`)}
                  error={errors?.series}
                />
              </Grid>
            )}

            {!isTradeIn && (
              <Grid
                size={{
                  xs: 6,
                  md: 2.5,
                }}
              >
                <InputTextField
                  disabled={!thirdPartySearchOverrideWatch}
                  label={t('worksheet.bodyStyle')}
                  {...register(`${name}.bodyStyle`)}
                  error={errors?.bodyStyle}
                />
              </Grid>
            )}

            {!isTradeIn && watch('vehicle.condition') !== ECarCondition.Used && (
              <Grid
                size={{
                  xs: 12,
                  md: 2,
                }}
              >
                <InputTextField
                  label={t('worksheet.msrp')}
                  {...register(`${name}.msrp`)}
                  error={errors?.msrp}
                  disabled={!thirdPartySearchOverrideWatch}
                />
              </Grid>
            )}

            {/* Trade-In */}
            {isTradeIn && (
              <>
                <Grid
                  size={{
                    xs: 6,
                    md: 2.5,
                  }}
                >
                  <InputTextField
                    label={t('worksheet.allowance')}
                    {...register(`${name}.allowance`)}
                    error={errors?.allowance}
                  />
                </Grid>

                <Grid
                  size={{
                    xs: 6,
                    md: 3,
                  }}
                >
                  <InputTextField
                    label={t('worksheet.lienAmount')}
                    {...register(`${name}.lienAmount`)}
                    error={errors?.lienAmount}
                  />
                </Grid>

                <Grid
                  size={{
                    xs: 6,
                    md: 9,
                  }}
                >
                  <InputTextField
                    label={t('worksheet.balanceOwedTo')}
                    {...register(`${name}.balanceOwedTo`)}
                    error={errors?.balanceOwedTo}
                  />
                </Grid>
              </>
            )}
          </>
        )}
      </Grid>
    </>
  )
}

export default VehicleForm
